/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Component, ElementRef, Inject, Injector, OnInit, Renderer2, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import * as _ from 'underscore';
import { environment } from '@env/environment';
import { AppConfigService } from '@app/common/app-config.service';
import { WindowRef } from '@app/common/window-ref.service';
import { AuthenticationService, LoginContext } from '@app/core/authentication';
import { Logger, MessageService } from '@core/lib/services';
import { TranslateService } from '@ngx-translate/core';
import { appConstants } from '@app/common/app-constants';
import { LogLevel } from '@core/lib/services/log-entry';
import { HttpHeaders } from '@angular/common/http';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { UtilService } from '@app/util/util.service';
import { NotifyService } from '@app/common/notify.service';
import { DataValidationService } from '@app/util/data-validation.service';
import { DOCUMENT } from '@angular/common';
import { IntegrationsService } from '@app/di/integrations/integrations.service';
import { isValidValue } from '@core/lib/helpers/helpers';
import { LoginService } from '@app/authentication/login/login.service';
import { Subscription } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { SystemNotificationType } from '@core/public-api';

const log = new Logger('LoginComponent');

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  type: SystemNotificationType = SystemNotificationType.infoGeneral;
  showSSOUsageNotifiy;

  version: string | null = environment.version;
  loginForm!: FormGroup;
  clicked = false;
  hidePassword = 'password';
  invalidEntry = false;
  isMultiLoginError = false;
  isChangePasswordPage = false;
  loginPageNative = false;
  loginPageSSO = false;
  SSOFlag = false;
  pkceFlag = false;
  clientId: string | null = null;
  baseUrl = '';
  expirePage = false;
  externalLogin = false;
  externalLoginSpinner = false;
  jwtToken = '';
  serviceUnavailable = false;

  error: string | undefined;
  updatePasswordForm!: FormGroup;
  copyOfUpdatePasswordForm!: FormGroup;
  isLoading = false;
  hideOldPassword = 'password';
  hideNewPassword = 'password';
  hideConfirmPassword = 'password';
  updatePasswordFeature = true;
  passwordExpired;
  days;
  userName;
  notForDiagnosticUseMessageEnabled;
  isAccountLocked = false;

  changePasswordErrors = {
    username: '',
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  };
  private errorMap = {
    password_history_criteria_failed: '',
    password_min_length_criteria_failed: '',
    password_max_length_criteria_failed: '',
    password_policy_criteria_failed: '',
    generic_error: 'play.generic.error',
    'user not found': 'changepwd.userIdNotFound',
    'credential change error': 'changepwd.invlidCurrentPassword',
    'invalid request': 'changepwd.passwordChangeNotAllowed'
  };

  @ViewChild('oldpassword') oldPassword: ElementRef;
  @ViewChild('newpassword') newPassword: ElementRef;
  @ViewChild('confirmpassword') confirmPassword: ElementRef;
  validationMsg = '';
  regex = '';
  minLength = 6;
  maxLength = 128;
  historyCount = 0;

  loginErrorMessage = '';
  changePasswordErrorMessage = '';
  isMultiSessionRetryError = false;
  multiSessionRetryWaitMessage = '';
  serviceUserloginErrorMessage = '';

  @ViewChild('loginUsername') loginUsername: ElementRef;
  @ViewChild('passwordField') passwordField: ElementRef;
  @ViewChild('reloginerror') reloginerror: ElementRef;

  private window: Window;
  private subscription: Subscription;
  authEndoint: any;
  callBackUrl: any;
  //TO-DO below value while cleanup
  //To-DO cookie clear
  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private authenticationService: AuthenticationService,
    private windowRef: WindowRef,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private dataService: ProxyDataService,
    private notify: NotifyService,
    private util: UtilService,
    private dataValidationService: DataValidationService,
    private messageService: MessageService,
    @Inject(DOCUMENT) private document: any,
    private injector: Injector,
    private loginService: LoginService,
    private cookieService: CookieService
  ) {
    this.createLoginForm();
    this.createForm();

    this.window = (this.document as Document).defaultView;
  }

  ngOnInit() {
    const appConfig = this.windowRef.nativeWindow[`appConfig`];
    this.subscription = this.route.queryParams.subscribe(params => {
      this.authenticationService.queryParams = params;
      this.initExternalLaunchWorkflow(params);
      appConstants.queryParams.forEach(param => {
        if (param === 'logLevel') {
          appConfig[param] = this.parseLogLevel(params[param]);
        } else if (param === 'reportType') {
          appConfig[param] = params[param] ? String(params[param]).toLowerCase() : undefined;
        } else {
          appConfig[param] = String(params[param]).toLowerCase() === 'true';
        }
      });
    });

    if (!this.util.isBrowserCompatible()) {
      this.notify.critical('Browser is not supported', 'Not Supported Browser');
    }
    if (this.util.getSystemProp()[appConstants.system_properties.UPDATE_PASSWORD_FEATURE] === 'disable') {
      this.updatePasswordFeature = false;
    }

    // set lights off as default theme for login window.
    this.window['GEHC'].EDS.theme.setTheme('eds-lights-off');

    this.notForDiagnosticUseMessageEnabled =
      this.util.getSystemProp()[appConstants.system_properties.NOT_FOR_DIAGNOSTIC_USE_MESSAGE_ENABLED] === 'true';
    this.serviceUnavailable = null != this.util.getSystemProp()['SERVICE_UNAVAILABLE'];
    if (!this.serviceUnavailable) {
      this.isSSOUserEnabled();
    }
    this.ssoOrServiceUserErrorHandle();
  }

  private isSSOUserEnabled() {
    const systemProperty = this.util.getSystemProp();
    this.SSOFlag = systemProperty['SSO_Flag'] === 'true';
    this.pkceFlag = systemProperty['enablepkce'] === 'true';
    this.authEndoint = systemProperty['Auth_End_URL'];
    this.callBackUrl = systemProperty['callbackurl'];
    const { clientId, baseUrl } = this.extractClientIdAndPathname(this.authEndoint);
    this.clientId = clientId;
    this.baseUrl = baseUrl;
    if (this.SSOFlag === undefined || !this.SSOFlag || this.authEndoint === undefined || this.pkceFlag === undefined) {
      this.loginPageNative = true;
    } else {
      this.loginPageSSO = true;
      this.showSSOUsageNotifiy = true;
    }
  }

  //Display the error message for both SSO and service user login in a single location.
  private ssoOrServiceUserErrorHandle() {
    if (this.loginService.ssoLoginError) {
      this.serviceUserloginErrorMessage = this.loginService.ssoLoginError;
    }
  }
  closeNotification() {
    this.showSSOUsageNotifiy = false;
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
    this.clearSSOLoginError();
  }

  private initExternalLaunchWorkflow(params: any) {
    log.debug('Launch parameters received from external application are - ', params);
    if (
      isValidValue(params[appConstants.externalApp]) &&
      isValidValue(params[appConstants.uvip]) &&
      isValidValue(params[appConstants.messageId]) &&
      isValidValue(params[appConstants.username])
    ) {
      this.loginPageNative = false;
      this.loginPageSSO = false;
      this.externalLogin = true;
      this.externalLoginSpinner = true;
      this.util.externalLoginApp = params[appConstants.externalApp];
      const systemProp = this.util.getSystemProp();
      const desktopIntegrations = systemProp && systemProp[appConstants.desktopIntegrations];
      if (desktopIntegrations) {
        this.loginService.username = params[appConstants.username];
        this.appConfigService.setUserId(params[appConstants.username]);
        this.subscribeToJwtLoginError();
        const integrationsService = this.injector.get(IntegrationsService);
        integrationsService.initExternalLaunchWorkflow(desktopIntegrations, params);
      } else {
        this.displayJwtLoginError();
      }
    }
  }

  private subscribeToJwtLoginError() {
    this.messageService.on('jwtValidation.error').subscribe(errorResponse => {
      this.displayJwtLoginError(errorResponse);
    });
  }

  private displayJwtLoginError(errorResponse?) {
    this.externalLoginSpinner = false;
    let errorMessage = this.translate.instant('login.err.user.validation');
    if (errorResponse?.status === 500) {
      errorMessage = this.translate.instant('login.page.serverUnavailable');
      return this.setErrorMsg(errorMessage);
    }
    return this.setErrorMsg(errorMessage);
  }

  private parseLogLevel(logLevel) {
    logLevel = parseInt(logLevel);
    if (logLevel && logLevel in LogLevel) {
      return logLevel;
    }
    return LogLevel.ERROR;
  }

  togglePassword() {
    this.hidePassword = this.hidePassword === 'password' ? 'text' : 'password';
    this.passwordField.nativeElement.focus();
  }

  login() {
    this.isMultiSessionRetryError = false;
    this.userName = (this.loginForm.value as LoginContext).loginUsername;
    this.clicked = true;
    if (
      this.validateScript(this.loginForm.value.loginUsername) ||
      this.validateScript(this.loginForm.value.loginPassword)
    ) {
      this.clicked = false;
      const errorMessage = this.translate.instant('login.page.invalid');
      this.setErrorMsg(errorMessage);
    } else {
      const login$ = this.authenticationService.login(this.loginForm.value);
      login$.then(
        successResponse => {
          if (successResponse.userName) {
            this.userName = successResponse.userName;
          }
          this.loginService.username = this.userName;
          const expireDate = Number(successResponse.message);
          if (expireDate >= 0 || expireDate === -9999) {
            this.appConfigService.getPasswordExpired(String(expireDate)).then(res => {
              this.expirePage = true;
              this.loginPageNative = false;
              this.isChangePasswordPage = false;
              this.days = successResponse.message;
              this.passwordExpired = res['PASSWORDEXPIRED'];
              this.clicked = false;
            });
          } else {
            this.continue();
          }
        },
        (errorResponse: any) => {
          this.handleLoginError(errorResponse);
          this.clicked = false;
        }
      );
    }
  }

  // SSO code started

  loginSSO(): void {
    this.clearSSOLoginError();
    log.debug('SSO : User clicked SSO login');
    this.cookieService.set('ssouserlogin', 'true');
    this.cookieService.set('serviceuserlogin', 'false');
    this.authenticationService
      .initiateAuthRequestSSOUser(this.pkceFlag, this.baseUrl, this.clientId, false)
      .then(data => {
        window.location.href = data;
      })
      .catch(error => {
        Logger.prototype.error(error);
      });
  }

  loginServiceUser(): void {
    this.clearSSOLoginError();
    log.debug('SSO : User clicked ServiceUser login');
    this.cookieService.set('serviceuserlogin', 'true');
    this.cookieService.set('ssouserlogin', 'false');
    this.authenticationService
      .initiateAuthRequestServiceUser(this.pkceFlag, this.baseUrl, this.clientId, false)
      .then(data => {
        window.location.href = data;
      })
      .catch(error => {
        Logger.prototype.error(error);
      });
  }

  clearSSOLoginError() {
    this.loginService.ssoLoginError = '';
    this.serviceUserloginErrorMessage = '';
    this.loginService.ssoLoginError = undefined;
  }
  // SSO code ended

  handleLoginError(errorResponse) {
    let errorMessage = '';
    if (errorResponse?.status === 500 && errorResponse.error?.data?.message === 'service_unavailable') {
      errorMessage = this.translate.instant('login.page.serverUnavailable');
      return this.setErrorMsg(errorMessage);
    }

    if (errorResponse?.status === 500) {
      errorMessage = this.translate.instant('login.page.internalServerError');
      return this.setErrorMsg(errorMessage);
    }

    if (errorResponse?.status === 429) {
      errorMessage = this.translate.instant('login.page.tooManyLoginAttempts');
      return this.setErrorMsg(errorMessage);
    }

    if (errorResponse?.status === 401 && errorResponse.error?.errors?.unauthorizedError) {
      errorMessage = this.translate.instant('login.page.' + errorResponse.error.errors.unauthorizedError);
      if (errorResponse.error.errors.unauthorizedError == 'acct_locked') {
        this.isAccountLocked = true;
        const lockedTime = errorResponse.error?.errors?.lockedTime;
        return this.setErrorMsg(errorMessage.replace('[lockedMinutes]', lockedTime));
      }
      return this.setErrorMsg(errorMessage);
    }

    if (errorResponse?.status === 401 && errorResponse.error?.errors?.duplicateLogin) {
      this.isMultiLoginError = true;
      errorMessage = this.translate.instant('login.page.currentUserLoggedInError');
      return this.setErrorMsg(errorMessage);
    }

    if (errorResponse?.status === 401) {
      errorMessage = this.translate.instant('login.page.invalid_grant');
      return this.setErrorMsg(errorMessage);
    }

    if (errorResponse?.error?.multiSessionError) {
      this.multiSessionRetryWaitMessage = this.translate
        .instant('login.page.wait_retry')
        .replace('%1%', errorResponse?.error?.multiSessionRetryWaitTime);
      this.isMultiSessionRetryError = true;
      errorMessage = this.translate.instant(errorResponse?.error?.multiSessionError);
      return this.setErrorMsg(errorMessage);
    }

    const message = errorResponse?.error?.genericErrorMsg || 'play.generic.error';
    errorMessage = this.translate.instant(message);
    this.setErrorMsg(errorMessage);
  }

  resetLoginForm() {
    this.loginForm.reset();
    this.clearMsg();
  }

  resetUpdatePasswordForm() {
    this.updatePasswordForm.reset();
    this.changePasswordclearMsg();
  }

  changePasswordClicked() {
    this.isChangePasswordPage = true;
    this.loginPageNative = false;
    this.fetchChangePasswordProperties();
  }

  redirectToLoginPage() {
    localStorage.redirectToLogin = 'true';
  }

  fetchChangePasswordProperties() {
    this.appConfigService.getChangePasswordRegX().then(
      data => {
        this.regex = data['passwordRules'];
        this.validationMsg = data['MESSSAGE'];
        this.minLength = parseInt(data['minLength']);
        this.maxLength = parseInt(data['maxLength']);
        this.historyCount = parseInt(data['historyCount']);
        this.resetUpdatePasswordForm();

        // Translated string
        this.errorMap.password_history_criteria_failed = this.translate
          .instant('changepwd.invalidPasswordHistory')
          .replace(/#1/g, this.historyCount);
        this.errorMap.password_policy_criteria_failed = this.translate
          .instant('changepwd.invalidPassword')
          .replace(/validMsg#/g, this.validationMsg);

        this.errorMap.password_max_length_criteria_failed = this.errorMap.password_min_length_criteria_failed =
          this.translate
            .instant('changepwd.invalidPasswordLength')
            .replace(/min#/g, this.minLength)
            .replace(/max#/g, this.maxLength);
      },
      () => {
        this.showFlash('changepwd.page.emptyPwdRules');
      }
    );
  }

  private doFocus(eleRef: ElementRef<any>) {
    eleRef.nativeElement.focus();
  }

  clearMsg() {
    this.loginErrorMessage = '';
    this.invalidEntry = false;
  }

  setErrorMsg(errorMsg) {
    this.loginErrorMessage = errorMsg;
    this.invalidEntry = true;
  }

  private createLoginForm() {
    this.loginForm = this.formBuilder.group({
      loginUsername: ['', Validators.required],
      loginPassword: ['', Validators.required]
    });
  }

  encodeUpdatePasswordForm() {
    this.copyOfUpdatePasswordForm = _.clone(this.updatePasswordForm, true);
    this.copyOfUpdatePasswordForm.value.oldPassword = encodeURIComponent(
      this.copyOfUpdatePasswordForm.value.oldPassword
    );
    this.copyOfUpdatePasswordForm.value.confirmNewPassword = encodeURIComponent(
      this.copyOfUpdatePasswordForm.value.confirmNewPassword
    );
    this.copyOfUpdatePasswordForm.value.newPassword = encodeURIComponent(
      this.copyOfUpdatePasswordForm.value.newPassword
    );
  }

  changePassword() {
    this.changePasswordclearMsg();
    const inalidForm = this.formValidation();
    if (inalidForm) {
      const httpOptions = {
        headers: new HttpHeaders({
          'content-type': 'Application/Json'
        })
      };
      this.isLoading = true;
      this.encodeUpdatePasswordForm();
      const login$ = this.dataService.httpPost(
        appConstants.appURLs.changePassword,
        this.copyOfUpdatePasswordForm.value,
        httpOptions
      );
      login$.then(
        () => {
          this.isLoading = false;
          this.loginPageNative = true;
          this.isChangePasswordPage = false;
          this.resetLoginForm();
        },
        err => {
          this.isLoading = false;
          log.error('Update password failed' + JSON.stringify(err));

          let errorCode = this.errorMap.generic_error;
          if (err.error.errors) {
            errorCode = err.error.errors[0].title.toLowerCase();

            if (errorCode && errorCode == 'account is locked') {
              errorCode = this.translate.instant('changepwd.page.acct_locked');
            }
            if (this.errorMap[errorCode]) {
              errorCode = this.errorMap[errorCode];
            }
          }
          this.showFlash(errorCode);
        }
      );
    }
  }

  private formValidation() {
    this.showFlash('');

    const loginUsername = this.updatePasswordForm.get('loginUserName');
    if (loginUsername.value === '') {
      this.changePasswordErrors.username = 'changepwd.usernameEmptyMsg';
      this.doFocus(this.loginUsername);
      this.changeBorderColor(this.loginUsername);
      return false;
    }
    const oldPassword = this.updatePasswordForm.get('oldPassword');
    if (oldPassword.value === '') {
      this.changePasswordErrors.oldPassword = 'changepwd.oldPwdEmptyMsg';
      this.doFocus(this.oldPassword);
      this.changeBorderColor(this.oldPassword);
      return false;
    }
    const newPassword = this.updatePasswordForm.get('newPassword');
    if (newPassword.value === '') {
      this.changePasswordErrors.newPassword = 'changepwd.newPwdEmptyMsg';
      this.doFocus(this.newPassword);
      this.changeBorderColor(this.newPassword);
      return false;
    }
    const confirmNewPassword = this.updatePasswordForm.get('confirmNewPassword');
    if (confirmNewPassword.value === '') {
      this.changePasswordErrors.confirmNewPassword = 'changepwd.cnfNewPwdEmptyMsg';
      this.doFocus(this.confirmPassword);
      this.changeBorderColor(this.confirmPassword);
      return false;
    }
    if (this.validateScript(loginUsername.value)) {
      this.changePasswordErrors.username = 'changepwd.page.invalidInput';
      this.doFocus(this.loginUsername);
      this.changeBorderColor(this.loginUsername);
      return false;
    }
    if (this.validateScript(oldPassword.value)) {
      this.changePasswordErrors.oldPassword = 'changepwd.page.invalidInput';
      this.doFocus(this.oldPassword);
      this.changeBorderColor(this.oldPassword);
      return false;
    }
    if (this.validateScript(newPassword.value)) {
      this.changePasswordErrors.newPassword = 'changepwd.page.invalidInput';
      this.doFocus(this.newPassword);
      this.changeBorderColor(this.newPassword);
      return false;
    }
    if (this.validateScript(confirmNewPassword.value)) {
      this.changePasswordErrors.confirmNewPassword = 'changepwd.page.invalidInput';
      this.doFocus(this.confirmPassword);
      this.changeBorderColor(this.confirmPassword);
      return false;
    }
    if (newPassword.value.length < this.minLength || newPassword.value.length > this.maxLength) {
      this.changePasswordErrors.newPassword = this.translate
        .instant('changepwd.invalidPasswordLength')
        .replace(/min#/g, this.minLength)
        .replace(/max#/g, this.maxLength);
      this.clearChangeBorderFocus(newPassword, confirmNewPassword);
      return false;
    }
    if (!newPassword.value.match(this.regex)) {
      this.changePasswordErrors.newPassword = this.translate
        .instant('changepwd.invalidPassword')
        .replace(/validMsg#/g, this.validationMsg);
      this.clearChangeBorderFocus(newPassword, confirmNewPassword);
      return false;
    }

    if (newPassword.value === oldPassword.value) {
      this.changePasswordErrors.newPassword = 'changepwd.oldPasswordErrMsg';
      this.clearChangeBorderFocus(newPassword, confirmNewPassword);
      return false;
    }
    if (newPassword.value === loginUsername.value) {
      this.changePasswordErrors.newPassword = 'changepwd.usernamePwdMatchErrMsg';
      this.clearChangeBorderFocus(newPassword, confirmNewPassword);
      return false;
    }
    if (newPassword.value !== confirmNewPassword.value) {
      this.changePasswordErrors.confirmNewPassword = 'changepwd.passwordMatchErrMsg';
      this.clearChangeBorderFocus(newPassword, confirmNewPassword);
      return false;
    }
    return true;
  }

  validateScript(value: string) {
    return this.dataValidationService.hasSqlInjectionChar(value);
  }

  clearError() {
    this.showFlash('');
  }

  private clearChangeBorderFocus(newPassword: AbstractControl, confirmNewPassword: AbstractControl) {
    this.doFocus(this.newPassword);
    this.changeBorderColor(this.newPassword);
    this.changeBorderColor(this.confirmPassword);
    this.clearValue(newPassword, confirmNewPassword);
  }

  private clearValue(newPassword: AbstractControl, confirmPassword: AbstractControl) {
    newPassword.setValue('');
    confirmPassword.setValue('');
  }

  private changeBorderColor(eleRef: ElementRef<any>) {
    this.renderer.setAttribute(eleRef.nativeElement, 'aria-invalid', 'true');
  }

  private showFlash(msg: string) {
    this.changePasswordErrors = {
      username: '',
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    };

    this.changePasswordErrorMessage = msg;
  }

  toggleOldPassword() {
    this.hideOldPassword = this.hideOldPassword === 'password' ? 'text' : 'password';
  }

  toggleNewPassword() {
    this.hideNewPassword = this.hideNewPassword === 'password' ? 'text' : 'password';
  }

  toggleConfirmPassword() {
    this.hideConfirmPassword = this.hideConfirmPassword === 'password' ? 'text' : 'password';
  }

  cancel() {
    this.isChangePasswordPage = false;
    this.loginPageNative = true;
  }

  changePasswordclearMsg() {
    this.showFlash('');
    this.renderer.removeAttribute(this.loginUsername.nativeElement, 'aria-invalid');
    this.renderer.removeAttribute(this.oldPassword.nativeElement, 'aria-invalid');
    this.renderer.removeAttribute(this.newPassword.nativeElement, 'aria-invalid');
    this.renderer.removeAttribute(this.confirmPassword.nativeElement, 'aria-invalid');
  }

  private createForm() {
    this.updatePasswordForm = this.formBuilder.group({
      loginUserName: new FormControl('', [
        Validators.required
        // Validators.pattern('(<|%3c|&lt)script')
      ]),
      oldPassword: new FormControl('', [Validators.required]),
      newPassword: new FormControl('', [Validators.required]),
      confirmNewPassword: new FormControl('', [Validators.required])
    });
  }

  continue() {
    this.loginService.username = this.userName;
    this.loginService.navigateToLoginSuccessPage();
  }

  changePasswordForm() {
    this.expirePage = false;
    this.isChangePasswordPage = true;
    this.loginPageNative = false;
    this.updatePasswordForm.reset();
  }

  matchPassword() {
    const newPassword = this.updatePasswordForm.get('newPassword');
    const confirmNewPassword = this.updatePasswordForm.get('confirmNewPassword');
    if (!newPassword.value.match(this.regex)) {
      this.changePasswordErrors.newPassword = this.translate
        .instant('changepwd.invalidPassword')
        .replace(/validMsg#/g, this.validationMsg);
    } else {
      this.changePasswordErrors.newPassword = undefined;
    }

    if (newPassword.value !== confirmNewPassword.value) {
      this.changePasswordErrors.confirmNewPassword = 'changepwd.passwordMatchErrMsg';
    } else {
      this.changePasswordErrors.confirmNewPassword = undefined;
    }
  }

  private extractClientIdAndPathname(fullUrl: string): { clientId: string | null; baseUrl: string } {
    try {
      // Create a URL object
      const parsedUrl = new URL(fullUrl);
      const baseUrl = `${parsedUrl.origin}${parsedUrl.pathname}`;
      // Extract the client_id from the query parameters
      const clientId = parsedUrl.searchParams.get('client_id');

      log.info('baseUrl for IDP ' + baseUrl);
      return { clientId, baseUrl };
    } catch (error) {
      console.error('Invalid URL:', error);
      return { clientId: null, baseUrl: '' };
    }
  }
}
